import type { GeoLocation } from '@bambi/api-client/schemas';
import { Button } from '@bambi/component-library/src/components/Button/Button';
import { WizardHeader } from '@bambi/component-library/src/components/Wizard/deprecated/WizardHeader';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import {
  setSelectedGeolocation,
  setShowGeolocationModal,
} from '../../setting.slice';
import { assignDefaultFormValues } from './defaultFormValues';
import type { OnSelectLocation } from './GeolocationMapView';
import { GeolocationMapView } from './GeolocationMapView';
import { makeLngLat } from './helpers';
import { useGeolocationActions } from './hooks';
import type {
  ArchiveGeolocationCompletion,
  CreateUpdateGeolocationCompletion,
  GeolocationFormValues,
} from './types';
import { useAppDispatch } from 'src/app/store/hooks';
import type { RootState } from 'src/app/store/types';
import { ArchiveAction } from 'src/common/ArchiveAction';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { FormField } from 'src/common/FormField';
import { FormFieldController } from 'src/common/FormField/FormFieldController';
import { Modal } from 'src/common/primitives/Modal';

export const geolocationModalWidth = 'w-[600px]';
export const geolocationModalHeight = 'h-[70vh]';

export function GeolocationFormModal() {
  const dispatch = useAppDispatch();
  const { showGeolocationModal, selectedGeolocation } = useSelector(
    (state: RootState) => state.setting
  );

  const formTitle = useMemo(() => {
    return selectedGeolocation?.id
      ? 'Edit Geolocation'
      : 'Add a New Geolocation';
  }, [selectedGeolocation]);

  const handleClose = useCallback(() => {
    dispatch(setShowGeolocationModal(false));
    dispatch(setSelectedGeolocation(undefined));
  }, [dispatch]);

  return (
    <>
      <Modal
        open={showGeolocationModal}
        setOpen={(val) => dispatch(setShowGeolocationModal(val))}
        onClose={handleClose}
        data-testid="geolocations-dialog"
        title={formTitle}
      >
        <div
          className={`flex ${geolocationModalHeight} w-full flex-col ${geolocationModalWidth}`}
        >
          <WizardHeader title={formTitle} />
          <div className="flex-1">
            <GeolocationFormContent
              selectedGeolocation={selectedGeolocation}
              onDismiss={handleClose}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

interface GeolocationFormContentProps {
  selectedGeolocation?: GeoLocation;
  onCreateUpdate?: CreateUpdateGeolocationCompletion;
  onDismiss: () => void;
}

export function GeolocationFormContent({
  selectedGeolocation,
  onCreateUpdate,
  onDismiss,
}: GeolocationFormContentProps) {
  const [error, setError] = useState('');
  const isEditing = useMemo(
    () => !!selectedGeolocation?.id,
    [selectedGeolocation]
  );

  const isArchived = useMemo(
    () => !!(selectedGeolocation as any)?.deleted_at,
    [selectedGeolocation]
  );

  const defaultFormValues = useMemo(
    () => assignDefaultFormValues(selectedGeolocation),
    [selectedGeolocation]
  );

  const methods = useForm<GeolocationFormValues>({
    defaultValues: defaultFormValues,
  });
  const { reset: resetForm } = methods;

  const teardownModal = useCallback(() => {
    resetForm();
    onDismiss();
  }, [resetForm, onDismiss]);

  const onCreateUpdateComplete: CreateUpdateGeolocationCompletion = useCallback(
    (result, changeType) => {
      onCreateUpdate?.(result, changeType);
      teardownModal();
    },
    [onCreateUpdate, teardownModal]
  );

  const onArchiveComplete: ArchiveGeolocationCompletion = useCallback(() => {
    teardownModal();
  }, [teardownModal]);

  const onUnarchiveComplete: ArchiveGeolocationCompletion = useCallback(() => {
    teardownModal();
  }, [teardownModal]);

  const {
    submitGeolocation,
    archiveGeolocation,
    unarchiveGeolocation,
    isSubmitting,
  } = useGeolocationActions({
    onError: setError,
    onCreateUpdateComplete,
    onArchiveComplete,
    onUnarchiveComplete,
  });

  const onSelectLocation: OnSelectLocation = useCallback(
    (coordinates) => {
      methods.setValue('coordinates', coordinates);
    },
    [methods]
  );

  const handleUnarchiveGeolocation = useCallback(
    (id: string) => {
      unarchiveGeolocation(id, selectedGeolocation);
    },
    [unarchiveGeolocation, selectedGeolocation]
  );

  const onArchive = useMemo(() => {
    if (isEditing && selectedGeolocation?.id && !isArchived) {
      return () => archiveGeolocation(selectedGeolocation.id);
    }
    return undefined;
  }, [isEditing, archiveGeolocation, selectedGeolocation, isArchived]);

  const onUnarchive = useMemo(() => {
    if (isEditing && selectedGeolocation?.id && isArchived) {
      return () => handleUnarchiveGeolocation(selectedGeolocation.id);
    }
    return undefined;
  }, [isEditing, handleUnarchiveGeolocation, selectedGeolocation, isArchived]);

  return (
    <div
      data-testid="geolocation-form-content"
      className="flex h-full flex-col"
    >
      <div className="mt-3 flex-1 pb-4">
        <GeolocationMapView
          onSelect={onSelectLocation}
          coordinates={makeLngLat(selectedGeolocation?.location)}
          canEditCoordinates={!isEditing}
        />
        {isEditing && (
          <div className="text-sm text-gray-500">
            For trip data integrity, coordinates cannot be edited.
          </div>
        )}
      </div>
      {error && (
        <div className="my-2">
          <FormErrorMessage>{error}</FormErrorMessage>
        </div>
      )}
      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            e.stopPropagation();
            methods.handleSubmit(submitGeolocation)(e);
          }}
        >
          <div className="my-4 flex">
            <FormFieldController
              name="label"
              rules={{ required: 'Geolocation label is required' }}
            >
              {({ field, fieldState }) => {
                return (
                  <FormField
                    label="Geolocation Label *"
                    error={fieldState.error?.message?.toString()}
                    type="text"
                    inputProps={{
                      id: 'label',
                      ...field,
                      'data-testid': 'geolocation-label',
                      placeholder:
                        'Description of the location (shown to driver)',
                    }}
                  />
                );
              }}
            </FormFieldController>
          </div>
          <CompletionButtons
            isSubmitting={isSubmitting}
            onCancel={teardownModal}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
          />
        </form>
      </FormProvider>
    </div>
  );
}

interface CompletionHandlers {
  onCancel: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
}

interface CompletionButtonsProps extends CompletionHandlers {
  isSubmitting: boolean;
}

const completionButtonWidth = 'w-32';

export const CompletionButtons = ({
  isSubmitting,
  onCancel,
  onArchive,
  onUnarchive,
}: CompletionButtonsProps) => {
  return (
    <>
      <div className="mt-8 flex flex-row justify-end gap-4">
        {onArchive && (
          <ArchiveAction
            onClick={onArchive}
            label="Archive"
            className={completionButtonWidth}
          />
        )}
        {onUnarchive && (
          <ArchiveAction
            onClick={onUnarchive}
            label="Unarchive"
            className={completionButtonWidth}
          />
        )}
        <Button
          onClick={onCancel}
          disabled={isSubmitting}
          className={completionButtonWidth}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className={completionButtonWidth}
          variant="primary"
          disabled={isSubmitting}
          dataTestId="save-geolocation-button"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </>
  );
};
